'use client';

import React, { FC, useEffect } from 'react';
import { InventoryStatusCode } from '@/lib/shopify';
import { Button } from '@/components';
import useProductContext from '@/context/ProductContext';
import { TypeProduct } from '@/core/generated-types';
import { useProductValidations } from '@/hooks/useProductValidations';
import { CTA_COLORS } from '../types';
import { getCTAStyles } from '@/components/utils';

interface AddButtonProps {
  inventoryStatus: InventoryStatusCode | null;
  onClick?: () => void;
  isSelected?: boolean;
  isLoading?: boolean;
  product?: TypeProduct | null;
  isDisabledFromCaller?: boolean;
  dataTestId?: string;
  hasAddToCartText?: boolean;
}

const inventoryStatusFallbacks = {
  outOfStock: 'Out of Stock',
  availableCta: 'Add to Cart',
  outOfStockCta: 'Out of Stock',
  preSaleCta: 'Pre-Order Now',
  addedToOrder: 'Added to Order',
};

const AddButton: FC<AddButtonProps> = ({
  inventoryStatus,
  onClick,
  isSelected = false,
  product,
  isLoading = false,
  isDisabledFromCaller = false,
  dataTestId,
  hasAddToCartText = false,
}) => {
  const { inventoryStatusCopy } = useProductContext();

  const isValidProduct = useProductValidations(product);
  const isDisabledByValidation = !isValidProduct;
  let isDisabled = isDisabledByValidation;

  const ctaColor = CTA_COLORS.DEFAULT;

  let buttonText = '';
  let buttonClasses = '';

  // Get validation message from product translations if available
  const ctaValidationMsg =
    product?.fields?.translations?.fields?.ctaValidationMsg;

  // This logic ensures submit buttons outside ths scope of the add to cart form align with the cta text assigned here.
  useEffect(() => {
    const submitButtons = document.querySelectorAll(
      'button[type="submit"][form="add-to-cart-form"]',
    );

    submitButtons.forEach((button) => {
      button.textContent = buttonText;
    });
  }, [buttonText]);

  const defaultAddToCartText =
    inventoryStatusCopy?.availableCta || inventoryStatusFallbacks.availableCta;

  const addToCartText = product?.fields?.addToCartText ?? defaultAddToCartText;

  const addedToOrderText =
    inventoryStatusCopy?.addedToOrder || inventoryStatusFallbacks.addedToOrder;

  switch (inventoryStatus) {
    default: {
      buttonText = isSelected ? addedToOrderText : addToCartText;
      buttonClasses = getCTAStyles(ctaColor);
      break;
    }
    case InventoryStatusCode.OUT_OF_STOCK: {
      buttonText =
        inventoryStatusCopy?.outOfStockCta ||
        inventoryStatusFallbacks.outOfStockCta;
      isDisabled = true;
      break;
    }
    case InventoryStatusCode.PRESALE: {
      buttonText =
        inventoryStatusCopy?.preSaleCta || inventoryStatusFallbacks.preSaleCta;
      break;
    }
  }

  // Override button text with validation message when disabled by product validation
  if (isDisabledByValidation && ctaValidationMsg) {
    buttonText = ctaValidationMsg;
  }

  if (isDisabled) {
    buttonClasses = '!bg-grey-light !border-grey-light';
  }

  return (
    <Button
      type="submit"
      ctaText={hasAddToCartText ? defaultAddToCartText : buttonText}
      fullWidth={true}
      classes={`btn-responsive ${buttonClasses}`}
      disabled={isDisabled || isDisabledFromCaller || isSelected || isLoading}
      includeSpinner={isLoading}
      onClick={onClick}
      testId={dataTestId}
    />
  );
};

export default AddButton;
